import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, Trash2, BellDot, Search, Send } from "lucide-react";

export default function SavedSearches() {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);

  const load = async () => {
    setLoading(true);
    try { const { data } = await api.get("/saved-searches"); setList(data); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const remove = async (id) => {
    if (!window.confirm("Delete this saved search?")) return;
    try { await api.delete(`/saved-searches/${id}`); setList((l) => l.filter((x) => x.id !== id)); toast.success("Deleted"); }
    catch { toast.error("Delete failed"); }
  };
  const runNow = async () => {
    setSending(true);
    try {
      const { data } = await api.post("/saved-searches/run-digest");
      toast.success(`Digest run — sent ${data.sent}, skipped ${data.skipped}`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Digest failed"); }
    finally { setSending(false); }
  };

  return (
    <div className="max-w-5xl mx-auto px-5 md:px-8 py-10">
      <div className="flex flex-wrap items-end justify-between gap-3 mb-6">
        <div>
          <div className="label-eyebrow">Saved job alerts</div>
          <h1 className="text-4xl md:text-5xl font-black tracking-tighter">Weekly email digests</h1>
          <p className="mt-2 text-gray-600 max-w-2xl">Every Monday at 09:00 UTC, we email you fresh remote jobs matching each saved search — sorted by fit if you have a saved resume.</p>
        </div>
        <button data-testid="digest-run-btn" onClick={runNow} disabled={sending}
          className="nb-btn bg-[#4338ca] text-white px-4 py-2 text-sm inline-flex items-center gap-2">
          {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Send my digest now
        </button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16"><Loader2 className="w-8 h-8 animate-spin" /></div>
      ) : list.length === 0 ? (
        <div className="nb-card p-10 text-center">
          <BellDot className="w-10 h-10 mx-auto text-[#eab308]" />
          <p className="mt-3 font-bold text-lg">No saved searches yet</p>
          <p className="text-gray-600 text-sm mt-1">Go to the Jobs page, set a search or tag, and click "Save this search".</p>
        </div>
      ) : (
        <div data-testid="searches-list" className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {list.map((s) => (
            <div key={s.id} data-testid={`search-${s.id}`} className="nb-card p-5">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="text-xs font-bold uppercase tracking-widest text-gray-500">Alert</div>
                  <h3 className="font-bold text-lg leading-snug">{s.name}</h3>
                </div>
                <button data-testid={`del-${s.id}`} onClick={() => remove(s.id)} className="nb-pill bg-white" title="Delete"><Trash2 className="w-3.5 h-3.5" /></button>
              </div>
              <div className="mt-3 flex flex-wrap gap-2 text-xs">
                {s.query && <span className="nb-pill"><Search className="w-3 h-3 mr-1" />{s.query}</span>}
                {s.tag && <span className="nb-pill bg-[#eab308]">#{s.tag}</span>}
                {s.work_type === "remote" && <span className="nb-pill bg-[#22c55e] text-white">Remote only</span>}
                {s.country && <span className="nb-pill bg-[#4338ca] text-white">{s.country}</span>}
                {s.city && <span className="nb-pill bg-[#ec4899] text-white">{s.city}</span>}
                {s.min_salary > 0 && <span className="nb-pill bg-[#0caa41] text-white">${(s.min_salary/1000).toFixed(0)}k+</span>}
                {s.posted_within_days > 0 && <span className="nb-pill bg-[#eab308] text-black">Last {s.posted_within_days}d</span>}
                {s.tz && <span className="nb-pill bg-[#4338ca] text-white">{s.tz.split("/").pop().replace(/_/g," ")} ±{s.tz_delta || 6}h</span>}
                {s.min_score > 0 && <span className="nb-pill bg-[#22c55e] text-white">{s.min_score}%+ fit</span>}
              </div>
              <div className="mt-3 text-xs text-gray-500">
                {s.last_sent_at ? `Last email: ${new Date(s.last_sent_at).toLocaleDateString()}` : "No emails sent yet"}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
